import React, { useState } from 'react';
import { StorageService } from '../services/storage';
import { X } from 'lucide-react';

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SignInModal: React.FC<SignInModalProps> = ({ isOpen, onClose }) => {
  const [currentUser, setCurrentUser] = useState(StorageService.getUser());
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [plan, setPlan] = useState<'Free' | 'Pro' | 'Business'>('Pro');

  if (!isOpen) return null;

  const handleSignIn = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    const user = StorageService.setUser({
      id: `u_${Date.now()}`,
      name: name.trim(),
      email: email.trim(),
      plan: plan
    });
    setCurrentUser(user);
    onClose();
  };

  const handleSignOut = () => {
    StorageService.setUser(null);
    setCurrentUser(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-200 relative animate-scaleUp">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-slate-700 w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>

        {currentUser ? (
          <div className="space-y-5 text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center text-2xl font-black mx-auto shadow-md">
              {currentUser.name.charAt(0)}
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">{currentUser.name}</h3>
              <p className="text-xs text-slate-500">{currentUser.email}</p>
              <div className="inline-block mt-2 bg-blue-50 text-blue-700 text-xs font-bold px-3 py-1 rounded-full border border-blue-200">
                Plan: {currentUser.plan} Member
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100 flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold"
              >
                Close
              </button>
              <button
                onClick={handleSignOut}
                className="flex-1 py-2.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-bold"
              >
                Sign Out
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSignIn} className="space-y-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-blue-600">AppStatsView Account</span>
              <h3 className="text-xl font-black text-slate-900 mt-1">Sign In / Register</h3>
              <p className="text-xs text-slate-500 mt-1">Access your saved watchlists, custom collections, and intelligence alerts.</p>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">Your Name</label>
              <input
                type="text"
                placeholder="Leonardo C."
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full text-xs p-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">Work Email</label>
              <input
                type="email"
                placeholder="leonardo@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full text-xs p-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">Select Tier (Demo)</label>
              <select
                value={plan}
                onChange={(e) => setPlan(e.target.value as any)}
                className="w-full text-xs p-3 rounded-xl border border-slate-200 bg-slate-50 focus:outline-none"
              >
                <option value="Pro">Pro ($49/mo - Full Intelligence Access)</option>
                <option value="Business">Business ($199/mo - API & Team Access)</option>
                <option value="Free">Free (Starter Ranking Access)</option>
              </select>
            </div>

            <button
              type="submit"
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-2xl shadow-md transition-all mt-2"
            >
              Sign In to AppStatsView
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
